// ============================================================
//  LOTUS HOLISTIC WELLNESS — SITE-WIDE CONFIGURATION
//  Edit values here and they update across the entire site.
// ============================================================

export const SITE_CONFIG = {

  // ── Business Identity ──────────────────────────────────────
  businessName: 'Lotus Holistic Wellness',
  legalName: 'Lotus Holistic Wellness, LLC',
  tagline: 'Therapeutic Massage for Pain Relief, Relaxation & Whole-Body Wellness',
  email: 'hello@lotusholisticwellness.com',
  founded: '2018',

  // ── Phone ─────────────────────────────────────────────────
  // Change this ONE value to update every phone number on the site
  phone: {
    display: '(602) 845-9447',   // shown to visitors
    href: 'tel:6028459447',       // used in <a href>
  },

  // ── Studio Address ────────────────────────────────────────
  // Change this ONE value to update every address on the site
  address: {
    street: '1237 S Val Vista Dr',
    suite: '',                    // leave empty if none
    city: 'Mesa',
    state: 'AZ',
    zip: '85204',
    neighborhood: 'Val Vista Crossings',
    full: '1237 S Val Vista Dr, Mesa, AZ 85204',
    mapsEmbed: 'https://www.google.com/maps/embed/v1/place?key=AIzaSyD-9tSrke72PouQMnMX-a7eZSW0jkFMBWY&q=1237+S+Val+Vista+Dr,+Mesa,+AZ+85204&zoom=16',
  },

  // ── Hours ─────────────────────────────────────────────────
  hours: {
    weekdays: 'Mon–Sat: 9am – 7pm',
    weekend: 'Sunday: Closed',
    short: 'Mon–Sat · 9am – 7pm',
  },

  // ── Services ──────────────────────────────────────────────
  // studio: available in-studio | mobile: available as mobile
  services: [
    { name: 'Swedish Massage',     path: '/services/swedish-massage',     icon: 'ri-hand-heart-line',        studio: true,  mobile: true  },
    { name: 'Pain Relief Massage', path: '/services/pain-relief-massage', icon: 'ri-heart-pulse-line',       studio: true,  mobile: true  },
    { name: 'Cupping Therapy',     path: '/services/cupping-therapy',     icon: 'ri-contrast-drop-2-line',   studio: true,  mobile: true  },
    { name: 'Lymphatic Drainage',  path: '/services/lymphatic-drainage',  icon: 'ri-drop-line',              studio: true,  mobile: true  }, // available mobile
    { name: 'Prenatal Massage',    path: '/services/prenatal-massage',    icon: 'ri-parent-line',            studio: true,  mobile: true  },
    { name: 'Red Light Therapy',   path: '/services/red-light-therapy',   icon: 'ri-sun-line',               studio: true,  mobile: false }, // studio only
  ],

  // ── Pricing ───────────────────────────────────────────────
  pricing: {
    studio: [
      { duration: '60 Minutes', price: '$130', desc: 'Targeted relief session' },
      { duration: '75 Minutes', price: '$155', desc: 'Most popular choice' },
      { duration: '90 Minutes', price: '$180', desc: 'Full-body deep relaxation' },
    ],
    mobile: [
      { duration: '60 Minutes', price: '$160', desc: 'Targeted relief at home' },
      { duration: '75 Minutes', price: '$180', desc: 'Most popular mobile session' },
      { duration: '90 Minutes', price: '$220', desc: 'Full-body in-home relaxation' },
    ],
  },

  // ── Service Areas ─────────────────────────────────────────
  areas: [
    { name: 'Mesa',             path: '/areas/mesa',             featured: true  },
    { name: 'Gilbert',          path: '/areas/gilbert',          featured: false },
    { name: 'Chandler',         path: '/areas/chandler',         featured: false },
    { name: 'Tempe',            path: '/areas/tempe',            featured: false },
    { name: 'Scottsdale',       path: '/areas/scottsdale',       featured: false },
    { name: 'Phoenix',          path: '/areas/phoenix',          featured: false },
    { name: 'Apache Junction',  path: '/areas/apache-junction',  featured: false },
  ],

  // Drive times from each area to the studio
  driveTimes: [
    { city: 'Mesa',            time: '0–10 min',  note: "You're already here!" },
    { city: 'Gilbert',         time: '~15 min',   note: '' },
    { city: 'Chandler',        time: '~20 min',   note: '' },
    { city: 'Tempe',           time: '~15 min',   note: '' },
    { city: 'Scottsdale',      time: '~25 min',   note: '' },
    { city: 'Phoenix',          time: '~30 min',   note: '' },
    { city: 'Apache Junction', time: '~25 min',   note: '' },
  ],

  // ── Social Media ──────────────────────────────────────────
  social: {
    instagram: 'https://www.instagram.com/lotus_holisticmassage/',
    facebook: 'https://www.facebook.com/people/Lotus-Holistic-Wellness/61576904237405/',
  },

  // ── SEO / Meta ────────────────────────────────────────────
  seo: {
    baseUrl: 'https://lotusmassagemesa.com',
    defaultTitle: 'Lotus Holistic Wellness | Massage Therapy Mesa AZ',
    defaultDescription: 'Professional massage therapy in Mesa, AZ. Swedish, prenatal, pain relief, lymphatic drainage, cupping & red light therapy. Studio & mobile sessions available.',
    defaultOgImage: 'https://public.readdy.ai/ai/img_res/edited_d19750fd52ba11ca48d1017c042f10e3_f7209627.jpg',
    geo: {
      latitude: '33.3816',
      longitude: '-111.7477',
      region: 'US-AZ',
      placename: 'Mesa, Arizona',
    },
  },

  // ── Design Standards ──────────────────────────────────────
  colors: {
    dark: '#1C1C1A',
    gold: '#C9A96E',
    cream: '#F8F7F4',
  },
};

// Convenience helpers
export const PHONE = SITE_CONFIG.phone;
export const ADDRESS = SITE_CONFIG.address;
export const SERVICES = SITE_CONFIG.services;
export const STUDIO_SERVICES = SITE_CONFIG.services.filter((s) => s.studio);
export const MOBILE_SERVICES = SITE_CONFIG.services.filter((s) => s.mobile);
export const ALL_SERVICE_NAMES = SITE_CONFIG.services.map((s) => s.name);
export const BUSINESS_NAME = SITE_CONFIG.businessName;
export const SOCIAL = SITE_CONFIG.social;

// ============================================================
//  DESIGN STANDARDS — Service Page CTA Section
//  This is the approved standard for ALL service page CTA
//  sections. Every new service page must follow this exactly.
// ============================================================
//
//  BACKGROUND:       solid #1C1C1A (no photo, no gradient image)
//  MIN HEIGHT:       min-h-[420px] flex items-center
//  OVERFLOW:         relative overflow-hidden
//
//  DECORATIVE ELEMENTS (all pointer-events-none):
//  1. Top edge line:
//       <div className="absolute top-0 left-0 w-full h-px
//            bg-gradient-to-r from-transparent via-[#C9A96E] to-transparent" />
//
//  2. Diagonal gold hatching (3% opacity):
//       <div className="absolute inset-0 opacity-[0.03]"
//            style={{ backgroundImage: 'repeating-linear-gradient(
//              45deg, #C9A96E 0px, #C9A96E 1px,
//              transparent 1px, transparent 28px)' }} />
//
//  3. Right-side vertical gold bar:
//       <div className="absolute right-0 top-0 h-full w-px
//            bg-gradient-to-b from-transparent via-[#C9A96E]/40 to-transparent" />
//
//  4. Top-left corner ornament:
//       <div className="absolute top-6 left-8 lg:left-24 flex items-center gap-2">
//         <div className="w-6 h-px bg-[#C9A96E]/40" />
//         <div className="w-1 h-1 rounded-full bg-[#C9A96E]/40" />
//       </div>
//
//  5. Bottom-right corner ornament:
//       <div className="absolute bottom-6 right-8 lg:right-24 flex items-center gap-2">
//         <div className="w-1 h-1 rounded-full bg-[#C9A96E]/40" />
//         <div className="w-6 h-px bg-[#C9A96E]/40" />
//       </div>
//
//  6. Bottom edge line:
//       <div className="absolute bottom-0 left-0 w-full h-px
//            bg-gradient-to-r from-transparent via-[#C9A96E]/50 to-transparent" />
//
//  CONTENT WRAPPER:  relative z-10 w-full px-8 lg:px-24 py-20
//  CONTENT MAX-W:    max-w-2xl (left-aligned)
//
//  TYPOGRAPHY:
//    - Eyebrow:  text-[#C9A96E] text-[10px] tracking-[0.3em] uppercase mb-6
//    - Heading:  text-white font-light leading-[1.1] mb-6
//                clamp(2.2rem, 4vw, 3.8rem)
//                <em> in italic text-[#C9A96E]
//    - Body:     text-white/55 text-base leading-relaxed mb-10 max-w-md
//
//  BUTTONS (flex-wrap gap-4):
//    Primary:   px-8 py-4 bg-[#C9A96E] text-[#1a1a18] text-xs
//               tracking-[0.15em] uppercase font-medium
//               hover:bg-[#b8955a] transition-all duration-300
//    Secondary: px-8 py-4 border border-white/30 text-white text-xs
//               tracking-[0.15em] uppercase
//               hover:border-white/70 transition-all duration-300
//
// ============================================================
export const SERVICE_PAGE_CTA_STANDARD = 'dark-gold-accents-v1' as const;